/** Read a cookie value safely (browser only). */
export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;

  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));

  if (!cookie) return null;

  const value = cookie.split('=')[1];

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export default getCookie;

/** Parses cookie JSON against a key whitelist, to prevent DOM-based JSON injection. */
export function parseCookieJSON<T = Record<string, string>>(
  value: string | null | undefined,
  allowedKeys: string[] = [],
  fallback: T = {} as T
): T {
  if (!value || typeof value !== 'string') return fallback;

  try {
    const parsed = JSON.parse(value);

    if (typeof parsed !== 'object' || parsed === null) {
      return fallback;
    }

    if ((parsed as Record<string, unknown>)?.__proto__ || (parsed as Record<string, unknown>)?.constructor) {
      return fallback;
    }

    const filtered: Record<string, string> = {};
    for (const key of allowedKeys) {
      if (typeof (parsed as Record<string, unknown>)[key] === 'string') {
        filtered[key] = (parsed as Record<string, string>)[key];
      }
    }

    return (Object.keys(filtered).length ? filtered : fallback) as T;
  } catch {
    return fallback;
  }
}

/** Get + parse a cookie in one call. */
export function getParsedCookie<T = Record<string, string>>(
  name: string,
  allowedKeys: string[] = [],
  fallback: T = {} as T
): T {
  const value = getCookie(name);
  return parseCookieJSON(value, allowedKeys, fallback);
}

/** Parse the p13n cookie with whitelist validation (server-side). */
export function parseP13nCookie(
  raw: string | null | undefined,
  fallback = { uid: '', role: '' }
): { uid: string; role: string } {
  return parseCookieJSON(raw, ['uid', 'role'], fallback);
}

/** P13N personalization cookie (client-side). */
export const getP13nCookie = (fallback = { uid: '', role: '' }) =>
  getParsedCookie('_cs_p13n', ['uid', 'role'], fallback);

/** Marketo form cookie (loose structure allowed). */
export const getMarketoCookie = (fallback: Record<string, unknown> = {}): Record<string, unknown> => {
  const raw = getCookie('mktoFormData');
  if (!raw || typeof raw !== 'string') return fallback;

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;

    if (parsed?.__proto__ || parsed?.constructor) return fallback;

    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? parsed
      : fallback;
  } catch {
    return fallback;
  }
};
